// Cloud backup for signed-in users (server/cloud.ts): catches, pins, routes, profile, groups and their messages,
// with photos as small previews. Runs by itself at most every 6 hours when something has changed, and from the
// buttons in the Account panel. Restoring merges, like restoring a backup file.
import type { AccountInfo } from "../shared/model.ts";
import { $maybe, el, toast } from "./dom.ts";
import { t } from "./i18n.ts";
import { closeSheet, grow, openSheet, primaryButton } from "./sheet.ts";
import { catches } from "./state.ts";
import { lsGet, lsSet } from "./util.ts";
import { buildBackup, restoreBackup, type BackupFile } from "./backup.ts";
import { account, accountApi, refreshAccount, renderAccount, signedIn } from "./account.ts";
import { pins, routes } from "./map/places.ts";
import { groups } from "./groups/store.ts";

const AUTO_EVERY_MS = 6 * 36e5;
let busy = false;
export const cloudBusy = (): boolean => busy;

/** A fingerprint of what would be backed up, to skip automatic backups when nothing changed. */
function fingerprint(): string {
  const newest = (xs: { createdAt?: number; updatedAt?: number }[]) => Math.max(0, ...xs.map((x) => x.updatedAt ?? x.createdAt ?? 0));
  return [catches.length, newest(catches), pins.length, newest(pins), routes.length, groups.length,
    Math.max(0, ...groups.map((g) => g.lastAt || 0)), lsGet("profile", "")].join("|");
}
function status(text: string): void {
  const s = $maybe("#cloud-status");
  if (s) s.textContent = text;
}

/** Backs up now. `manual`: from the button (shows the result); otherwise quiet unless it fails. */
export async function cloudBackupNow(manual: boolean): Promise<boolean> {
  if (busy || !signedIn() || !navigator.onLine) return false;
  busy = true;
  renderAccount();
  status(t("cloudBackingUp"));
  try {
    const fp = fingerprint();
    const data = await buildBackup("thumb");
    const res = await accountApi("/api/backup", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(data) }, 120000);
    if (!res.ok) throw new Error("backup " + res.status);
    lsSet("cloudPrint", fp);
    lsSet("cloudAt", String(Date.now()));
    if (manual) toast(t("cloudDone"));
    return true;
  } catch (e) {
    console.warn("Cloud backup failed", e);
    if (manual) toast(t("cloudFailed"));
    return false;
  } finally {
    busy = false;
    await refreshAccount(); // shows the new backup's time and counts
    renderAccount();
  }
}

/** Restores the cloud backup into this phone (merging). */
export async function cloudRestore(): Promise<void> {
  if (busy || !signedIn()) return;
  busy = true;
  renderAccount();
  try {
    const res = await accountApi("/api/backup", {}, 120000);
    if (!res.ok) throw new Error("restore " + res.status);
    const n = await restoreBackup((await res.json()) as BackupFile);
    lsSet("cloudPrint", fingerprint()); // what's on the phone now matches the cloud
    toast(t("cloudRestored", n));
  } catch (e) { console.warn("Cloud restore failed", e); toast(t("cloudRestoreFailed")); }
  finally { busy = false; renderAccount(); }
}

/** Right after signing in: when the cloud has a backup and this phone is nearly empty, offer to restore it.
 *  Returns true when the offer was shown. */
export async function offerRestore(): Promise<boolean> {
  const b: AccountInfo["backup"] = account?.backup ?? null;
  if (!b || catches.length > 0 || pins.length > 0) return false;
  openSheet(t("cloudOfferTitle"), [el("p", { text: t("cloudOffer", b.catches, b.pins) })],
    [grow(), el("button", { class: "btn", type: "button", onclick: closeSheet }, t("nudgeLater")),
      primaryButton(t("cloudOfferYes"), () => { closeSheet(); void cloudRestore(); })]);
  return true;
}

/** The automatic backup: when signed in, something changed, and the last one is more than 6 hours old. */
function maybeAutoBackup(): void {
  if (!signedIn() || busy || document.visibilityState !== "visible") return;
  if (Date.now() - Number(lsGet("cloudAt", "0")) < AUTO_EVERY_MS) return;
  if (lsGet("cloudPrint", "") === fingerprint()) return;
  void cloudBackupNow(false);
}
export function cloudStart(): void {
  setTimeout(maybeAutoBackup, 20000);
  setInterval(maybeAutoBackup, 10 * 6e4);
}
